import { PRINT_BLEED_TINT_INSIDE_TRIM_PT } from '@/lib/document/print-bleed'
import type { BleedMetrics } from '../types/bleed-metrics'

export type PrintBleedTintBand = {
  key: string
  /** The four top-row bands carry `left-0 top-0` classes as well as their inline position. */
  anchoredTopLeft: boolean
  rect: { left: number; top: number; width: number; height: number }
}

/**
 * The yellow bands of the print-bleed tint ring, in paint order: `bleedMargin` pt outside the
 * dashed trim + `PRINT_BLEED_TINT_INSIDE_TRIM_PT` toward the page (36 pt per edge). Returns `null`
 * when the ring has no room to draw at all.
 *
 * Every band — the in-trim safety band (between the dashed trim and the live page) and the outer
 * band (between the dashed trim and the true bleed edge, plus the pasteboard-corner fillers that
 * complete its shape) — is gated on `showOutsideTrim`, fed from the editor's "Show Bleed Bar"
 * toggle.
 *
 * When `centerGap` > 0 (double spread, bindery gutter), the top/bottom in-trim rows are left out so
 * they don't paint under the gutter's own column. That column's fill and its adjoining in-trim
 * segments are `DoubleSpreadGutterTintAboveShapes`, not drawn here.
 */
export function computePrintBleedTintBands({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimWidth,
  trimHeight,
  centerGap,
  showOutsideTrim,
}: BleedMetrics & { centerGap: number; showOutsideTrim: boolean }): PrintBleedTintBand[] | null {
  if (bleedMargin <= 0) {
    return null
  }

  const insideTint = PRINT_BLEED_TINT_INSIDE_TRIM_PT
  const bandTotal = bleedMargin + insideTint

  if (bleedHeight < bandTotal * 2 || bleedWidth < bandTotal * 2) {
    return null
  }

  if (!showOutsideTrim) {
    return []
  }

  const bottomBandTop = bleedMargin + trimHeight - insideTint
  const sideH = bleedHeight - bandTotal * 2
  /** In-trim tint only spans the trim rect; pasteboard corners stay outside tint. */
  const trimInnerW = Math.max(0, Math.min(trimWidth, bleedWidth - 2 * bleedMargin))
  const topInnerLeft = bleedMargin
  const topInnerRightWingLeft = bleedMargin + trimInnerW
  const topRightWingW = Math.max(0, bleedWidth - topInnerRightWingLeft)
  const drawInTrimRows = trimInnerW > 0 && centerGap <= 0

  const band = (
    key: string,
    anchoredTopLeft: boolean,
    left: number,
    top: number,
    width: number,
    height: number
  ): PrintBleedTintBand => ({ key, anchoredTopLeft, rect: { left, top, width, height } })

  return [
    // Top: full-width outer strip, then pasteboard-corner filler (outer band only).
    band('top-outer', true, 0, 0, bleedWidth, bleedMargin),
    band('top-left-corner', true, 0, bleedMargin, bleedMargin, insideTint),
    // In-trim row (safety band).
    ...(drawInTrimRows
      ? [band('top-in-trim', true, topInnerLeft, bleedMargin, trimInnerW, insideTint)]
      : []),
    band('top-right-wing', true, topInnerRightWingLeft, bleedMargin, topRightWingW, insideTint),
    // Bottom: in-trim row (safety band) plus pasteboard-corner fillers and outer strip.
    ...(drawInTrimRows
      ? [band('bottom-in-trim', false, topInnerLeft, bottomBandTop, trimInnerW, insideTint)]
      : []),
    band(
      'bottom-right-wing',
      false,
      topInnerRightWingLeft,
      bottomBandTop,
      topRightWingW,
      insideTint
    ),
    band('bottom-left-corner', false, 0, bottomBandTop, bleedMargin, insideTint),
    band('bottom-outer', false, 0, bottomBandTop + insideTint, bleedWidth, bleedMargin),
    // Outer pasteboard left (outer band only).
    band('left-outer', false, 0, bandTotal, bleedMargin, sideH),
    // In-trim left/right (safety band).
    band('left-in-trim', false, bleedMargin, bandTotal, insideTint, sideH),
    band('right-in-trim', false, bleedWidth - bandTotal, bandTotal, insideTint, sideH),
    // Outer pasteboard right (outer band only).
    band('right-outer', false, bleedWidth - bleedMargin, bandTotal, bleedMargin, sideH),
  ]
}
